import { useGetTimePeriods } from '@shared/api/hooks/reports/periods';
import { InsightsTimePeriod, TimePeriod } from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { SimpleTooltip } from '@shared/components/simple-tooltip';
import { Button } from '@shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@shared/components/ui/popover';
import { cn } from '@shared/lib/utils';
import { cva } from 'class-variance-authority';
import { format, parseISO } from 'date-fns';
import { useMemo, useState } from 'react';

type PeriodRange = { start: TimePeriod; end: TimePeriod };

/** Grid sections: one per reporting year, its month periods as cells. */
type YearSection = { year: TimePeriod; months: TimePeriod[] };

/** Context describing a committed selection, for callers that track changes. */
export type PeriodRangeChangeMeta = {
  selectionType: 'year' | 'month_range';
  startDate: string;
  endDate: string;
};

const monthCell = cva(
  'flex h-8 items-center justify-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600/40',
  {
    variants: {
      state: {
        default:
          'border border-gray-100 bg-gray-00 text-gray-800 hover:border-primary-400 hover:bg-primary-100',
        inRange: 'border border-transparent bg-primary-200 text-primary-800',
        endpoint:
          'border border-primary-600 bg-primary-600 font-semibold text-gray-00 shadow-sm',
      },
    },
    defaultVariants: { state: 'default' },
  }
);

/** Order two periods into a {start, end} range, earliest first. */
function orderedRange(a: TimePeriod, b: TimePeriod): PeriodRange {
  return a.startDate <= b.startDate
    ? { start: a, end: b }
    : { start: b, end: a };
}

/** Concise boundary-dates label, e.g. "5 Jan 26 – 4 Jan 27". */
function formatRange(
  start: { startDate: string } | null | undefined,
  end: { endDate: string } | null | undefined
): string {
  if (!start || !end) return 'All time';
  return `${format(parseISO(start.startDate), 'd MMM yy')} – ${format(
    parseISO(end.endDate),
    'd MMM yy'
  )}`;
}

type Props = {
  /** Explicitly selected boundary TimePeriod uuids; null when the selection
   *  should follow the backend default. */
  start: string | null;
  end: string | null;
  /** Fired when the selection changes: boundary uuids for a committed range
   *  (with meta describing it), or nulls when reset to the default. */
  onChange: (
    start: string | null,
    end: string | null,
    meta?: PeriodRangeChangeMeta
  ) => void;
  /** The applied window echoed by the backend — shown (and highlighted) when
   *  no explicit selection is set. */
  defaultStart?: InsightsTimePeriod | null;
  defaultEnd?: InsightsTimePeriod | null;
};

/**
 * Controlled period range picker structured around the organisation's actual
 * reporting periods — one section per reporting year, its month periods as
 * cells (orgs may run offset or partial years). Pick a start month, then an
 * end month; the selection is reported as TimePeriod uuids via onChange. With
 * no explicit selection the picker adopts the backend's default window without
 * calling onChange, so the default keeps tracking the most recent reporting
 * year.
 */
export function PeriodRangePicker({
  start,
  end,
  onChange,
  defaultStart,
  defaultEnd,
}: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<PeriodRange | null>(null);
  // Whether the next click starts a fresh range (vs. closing the current one).
  const [picking, setPicking] = useState(false);
  const [hover, setHover] = useState<TimePeriod | null>(null);
  const { data: periods } = useGetTimePeriods();

  const sections: YearSection[] = useMemo(() => {
    if (!periods) return [];
    const years = periods
      .filter((period) => period.type === 'year')
      .sort((a, b) => a.startDate.localeCompare(b.startDate));
    return years.map((year) => ({
      year,
      months: periods
        .filter(
          (period) => period.type === 'month' && period.parentUuid === year.uuid
        )
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0)),
    }));
  }, [periods]);

  const periodByUuid = useMemo(
    () => new Map((periods ?? []).map((period) => [period.uuid, period])),
    [periods]
  );

  const hasExplicitSelection = start !== null && end !== null;

  // The committed selection: explicit uuids, else the backend default.
  // A boundary may be a YEAR period (deep links pass a reporting year as both
  // boundaries); the grid works in months, so expand it to the year's first
  // or last month for display and highlighting.
  const committed: PeriodRange | null = useMemo(() => {
    const asMonthBoundary = (
      period: TimePeriod | undefined,
      edge: 'start' | 'end'
    ): TimePeriod | undefined => {
      if (!period || period.type !== 'year') return period;
      const months =
        sections.find((section) => section.year.uuid === period.uuid)?.months ??
        [];
      return edge === 'start' ? months[0] : months[months.length - 1];
    };
    const startPeriod = asMonthBoundary(
      start ? periodByUuid.get(start) : undefined,
      'start'
    );
    const endPeriod = asMonthBoundary(
      end ? periodByUuid.get(end) : undefined,
      'end'
    );
    if (startPeriod && endPeriod) {
      return { start: startPeriod, end: endPeriod };
    }
    const echoStart = asMonthBoundary(
      defaultStart ? periodByUuid.get(defaultStart.uuid) : undefined,
      'start'
    );
    const echoEnd = asMonthBoundary(
      defaultEnd ? periodByUuid.get(defaultEnd.uuid) : undefined,
      'end'
    );
    return echoStart && echoEnd ? { start: echoStart, end: echoEnd } : null;
  }, [start, end, periodByUuid, defaultStart, defaultEnd, sections]);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      // Re-seed the draft from the committed selection each time we open, so
      // an abandoned edit doesn't linger.
      setDraft(committed);
      setPicking(false);
      setHover(null);
    }
    setOpen(next);
  };

  const commitRange = (
    next: PeriodRange,
    selectionType: 'year' | 'month_range'
  ) => {
    setDraft(next);
    setPicking(false);
    setOpen(false);
    onChange(next.start.uuid, next.end.uuid, {
      selectionType,
      startDate: next.start.startDate,
      endDate: next.end.endDate,
    });
  };

  const handleMonthClick = (period: TimePeriod) => {
    if (!draft || !picking) {
      setDraft({ start: period, end: period });
      setPicking(true);
      return;
    }
    commitRange(orderedRange(draft.start, period), 'month_range');
  };

  const handleYearClick = (section: YearSection) => {
    const first = section.months[0] ?? section.year;
    const last = section.months[section.months.length - 1] ?? section.year;
    commitRange({ start: first, end: last }, 'year');
  };

  // While picking, preview the range out to the hovered month.
  const preview: PeriodRange | null = useMemo(() => {
    if (!draft) return null;
    if (picking && hover) return orderedRange(draft.start, hover);
    return draft;
  }, [draft, picking, hover]);

  const cellState = (period: TimePeriod) => {
    if (!preview) return 'default' as const;
    if (
      period.uuid === preview.start.uuid ||
      period.uuid === preview.end.uuid
    ) {
      return 'endpoint' as const;
    }
    if (
      period.startDate > preview.start.startDate &&
      period.startDate < preview.end.startDate
    ) {
      return 'inRange' as const;
    }
    return 'default' as const;
  };

  const yearInRange = (section: YearSection) =>
    !!preview &&
    section.year.endDate >= preview.start.startDate &&
    section.year.startDate <= preview.end.startDate;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="h-10 justify-between gap-2 px-3 font-normal shadow-none"
          icon={<Icons.calendar />}
        >
          {formatRange(committed?.start, committed?.end)}
          <Icons.chevronDown className="ml-1 h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] overflow-hidden rounded-xl p-0 shadow-lg"
      >
        <div className="max-h-[62vh] space-y-4 overflow-y-auto p-4">
          {sections.map((section) => (
            <div key={section.year.uuid} className="space-y-2">
              <button
                type="button"
                onClick={() => handleYearClick(section)}
                className="block text-left transition-opacity hover:opacity-60"
              >
                <Text
                  variant="subSection"
                  className={cn(
                    'text-lg font-semibold',
                    yearInRange(section) ? 'text-primary-700' : 'text-gray-900'
                  )}
                >
                  {section.year.name}
                </Text>
              </button>
              <div className="grid grid-cols-6 gap-1.5">
                {section.months.map((month) => (
                  <SimpleTooltip
                    key={month.uuid}
                    content={month.name}
                    trigger={
                      <button
                        type="button"
                        onClick={() => handleMonthClick(month)}
                        onMouseEnter={() => setHover(month)}
                        onMouseLeave={() => setHover(null)}
                        className={cn(monthCell({ state: cellState(month) }))}
                        aria-label={month.name}
                        aria-pressed={cellState(month) !== 'default'}
                      >
                        {format(parseISO(month.startDate), 'MMM')}
                      </button>
                    }
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
